
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { AuthEmailResponse, handleEmailServiceError } from "./types";

/**
 * Service for password-related authentication emails
 */
export const passwordService = {
  /**
   * Trigger password reset email using Supabase's built-in functionality
   */
  async triggerPasswordReset(email: string): Promise<AuthEmailResponse> {
    try {
      logger.info({
        message: "Triggering password reset via Supabase",
        context: { email }
      });
      
      // Get the reset redirect URL for our custom flow
      const resetRedirectUrl = `${window.location.origin}/reset-password`;
      
      // Use Supabase's built-in password reset functionality
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetRedirectUrl,
      });

      if (error) {
        // Check specifically for rate limiting errors
        if (
          error.message?.toLowerCase().includes('rate limit') || 
          error.status === 429 ||
          error.message?.includes('email rate limit exceeded')
        ) {
          logger.warn({
            message: "Password reset rate limit detected",
            context: { 
              email,
              errorMessage: error.message 
            }
          });
          
          return { 
            success: false, 
            error: "For security reasons, we limit how often reset emails can be sent. Please wait 1 minute before trying again.",
            rateLimited: true 
          };
        }
        
        throw error;
      }
      
      logger.info({
        message: "Password reset email sent successfully via Supabase",
        context: { email }
      });
      
      return { success: true };
    } catch (error) {
      return handleEmailServiceError(error, { operation: "trigger password reset", email });
    }
  },
  
  /**
   * Log password changes (removed email sending implementation)
   */
  async sendPasswordChangedEmail(email: string): Promise<AuthEmailResponse> {
    try {
      // Just log the password change without sending an email through Resend
      logger.info({
        message: "Password changed",
        context: { email }
      });
      
      return { success: true };
    } catch (error) {
      return handleEmailServiceError(error, { operation: "log password changed", email });
    }
  }
};
